import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

export interface User {
  id: number;
  username: string;
  email: string;
  displayName?: string;
}

export interface AuthResult {
  verified: boolean;
  user?: User;
  message?: string;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class BiometricAuthService {
  private apiUrl = `${environment.apiBaseUrl}/auth`;

  constructor(private http: HttpClient) { }

  /**
   * Vérifier si le navigateur supporte WebAuthn
   */
  isSupported(): boolean {
    return window?.PublicKeyCredential !== undefined &&
      navigator?.credentials !== undefined;
  }

  /**
   * Vérifier si un utilisateur existe
   */
  async checkUserExists(username: string): Promise<{ exists: boolean; hasCredentials: boolean }> {
    try {
      return await firstValueFrom(
        this.http.get<{ exists: boolean; hasCredentials: boolean }>(`${this.apiUrl}/user/${username}`)
      );
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'utilisateur:', error);
      return { exists: false, hasCredentials: false };
    }
  }

  /**
   * Enregistrer une nouvelle empreinte digitale
   */
  async registerFingerprint(username: string, email: string, displayName?: string): Promise<AuthResult> {
    try {
      // 1. Obtenir les options du serveur
      const { options, userId } = await firstValueFrom(
        this.http.post<any>(`${this.apiUrl}/register/options`, {
          username,
          email,
          displayName
        })
      );

      // 2. Demander l'empreinte à l'utilisateur
      const credential = await startRegistration(options);

      // 3. Vérifier avec le serveur
      const verification = await firstValueFrom(
        this.http.post<AuthResult>(`${this.apiUrl}/register/verify`, {
          userId,
          credential
        })
      );

      return verification;
    } catch (error: any) {
      console.error('Erreur lors de l\'enregistrement:', error);

      if (error.name === 'NotAllowedError') {
        return { verified: false, error: 'Opération annulée par l\'utilisateur' };
      } else if (error.name === 'NotSupportedError') {
        return { verified: false, error: 'Authentification biométrique non supportée' };
      }

      return {
        verified: false,
        error: error.error?.error || 'Erreur lors de l\'enregistrement de l\'empreinte'
      };
    }
  }

  /**
   * Se connecter avec l'empreinte digitale
   */
  async authenticateWithFingerprint(username: string): Promise<AuthResult> {
    try {
      console.log('🌐 Appel API authenticate/options pour:', username);

      // 1. Obtenir les options d'authentification
      const { options, userId } = await firstValueFrom(
        this.http.post<any>(`${this.apiUrl}/authenticate/options`, { username })
      );

      console.log('✅ Options reçues, userId:', userId);
      console.log('🔑 Options:', options);

      // 2. Demander l'empreinte à l'utilisateur
      console.log('👆 Demande de l\'empreinte à l\'utilisateur...');
      const credential = await startAuthentication(options);

      console.log('✅ Credential obtenu:', credential);

      // 3. Vérifier avec le serveur
      console.log('🌐 Appel API authenticate/verify');
      const verification = await firstValueFrom(
        this.http.post<AuthResult>(`${this.apiUrl}/authenticate/verify`, {
          userId,
          credential
        })
      );

      console.log('✅ Vérification terminée:', verification);
      return verification;
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'authentification:', error);

      if (error.name === 'NotAllowedError') {
        return { verified: false, error: 'Opération annulée par l\'utilisateur' };
      } else if (error.name === 'NotSupportedError') {
        return { verified: false, error: 'Authentification biométrique non supportée' };
      }

      return {
        verified: false,
        error: error.error?.error || 'Erreur lors de l\'authentification'
      };
    }
  }

  /**
   * Vérifier la disponibilité des fonctionnalités biométriques
   */
  async checkBiometricAvailability(): Promise<{
    available: boolean;
    platformAuthenticator: boolean;
    conditionalMediation: boolean;
  }> {
    const result = {
      available: this.isSupported(),
      platformAuthenticator: false,
      conditionalMediation: false
    };

    if (!result.available) {
      return result;
    }

    try {
      // Vérifier si l'authentificateur de plateforme est disponible (Touch ID, Face ID, Windows Hello)
      result.platformAuthenticator = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();

      // Vérifier si la médiation conditionnelle est supportée (remplissage automatique)
      if (PublicKeyCredential.isConditionalMediationAvailable) {
        result.conditionalMediation = await PublicKeyCredential.isConditionalMediationAvailable();
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des capacités biométriques:', error);
    }

    return result;
  }
}
